import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppVersionPolicyModel, SaveAppVersionPolicyRequest } from '../../models/app-version-policy.model';
import { AppVersionService } from '../../services/app-version.service';

type PlatformKey = 'android' | 'ios';

@Component({
  selector: 'app-app-versions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app-versions.html',
  styleUrl: './app-versions.css'
})
export class AppVersionsComponent implements OnInit {
  policies: AppVersionPolicyModel[] = [];
  loading = true;
  savingPlatform: PlatformKey | null = null;
  message = '';
  error = '';

  readonly platformOrder: PlatformKey[] = ['android', 'ios'];

  constructor(private appVersionService: AppVersionService) {}

  ngOnInit(): void {
    this.loadPolicies();
  }

  loadPolicies(): void {
    this.loading = true;
    this.error = '';

    this.appVersionService.getPolicies().subscribe({
      next: policies => {
        this.policies = this.platformOrder.map(platform => {
          const existing = policies.find(policy => policy.platform === platform);
          return existing ?? this.createDefaultPolicy(platform);
        });
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.error = error?.error?.message || 'Unable to load app version policies.';
      }
    });
  }

  save(policy: AppVersionPolicyModel): void {
    this.message = '';
    this.error = '';

    const request: SaveAppVersionPolicyRequest = {
      platform: policy.platform,
      displayVersion: policy.displayVersion.trim(),
      buildNumber: Number(policy.buildNumber),
      forceUpdate: policy.forceUpdate,
      storeUrl: policy.storeUrl.trim(),
      isActive: policy.isActive
    };

    if (!request.displayVersion) {
      this.error = 'Display version is required.';
      return;
    }

    if (!request.buildNumber || request.buildNumber <= 0) {
      this.error = 'Build number must be greater than zero.';
      return;
    }

    if (request.forceUpdate && !request.storeUrl) {
      this.error = 'Store URL is required when force update is enabled.';
      return;
    }

    this.savingPlatform = policy.platform;
    this.appVersionService.savePolicy(request).subscribe({
      next: saved => {
        this.policies = this.policies.map(item => item.platform === saved.platform ? saved : item);
        this.savingPlatform = null;
        this.message = `${this.getPlatformLabel(saved.platform)} app version policy saved.`;
      },
      error: error => {
        this.savingPlatform = null;
        this.error = error?.error?.message || 'Unable to save app version policy.';
      }
    });
  }

  getPlatformLabel(platform: PlatformKey): string {
    return platform === 'android' ? 'Android' : 'iOS';
  }

  getBuildLabel(platform: PlatformKey): string {
    return platform === 'android' ? 'Version Code' : 'Build Number';
  }

  private createDefaultPolicy(platform: PlatformKey): AppVersionPolicyModel {
    return {
      appVersionPolicyId: 0,
      platform,
      displayVersion: '',
      buildNumber: 1,
      forceUpdate: false,
      storeUrl: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: null
    };
  }
}
